import {
  LivestockCohortRecordT,
  LivestockGenderT,
  LivestockTagColorT,
  LivestockCohortPurposeT,
} from '@ruminati/types_v2/livestock_cohorts'
import { ParsedSalePurchaseLineItemT } from '@ruminati/types_v2/livestock_documents'
import { LivestockEventRecordT } from '@ruminati/types_v2/livestock_events'
import { CattleBreedConfigT, SheepBreedConfigT } from '@ruminati/types_v2/livestock_system'
import dayjs from 'dayjs'
import { flat, pick, mergeDeep, clone, capitalize } from 'remeda'

import { CattleAgeClass, getCattleGenderAgeClassLabel } from './age-classes-cattle'
import { SheepAgeClass, getSheepGenderAgeClassLabel } from './age-classes-sheep'
import { HeadcountEvent, WeighingEvent } from './events'
import { getRatesForDate } from './valuation-rates-quarterly'
import {
  calculateDailyGrowth,
  getGrowthCurveConfig,
  getSeasonalConfig,
  GrowthCurveConfig,
  MarketCondition,
  SeasonalConfig,
} from './weight'

export type LifeCycleEvents = {
  weaned?: Date

  // Female events
  firstJoined?: Date

  // Male events
  castrated?: Date
}

export type AgeClassPeriod<SpeciesAgeClass extends CattleAgeClass | SheepAgeClass> = {
  ageClass: SpeciesAgeClass
  startDate: Date
  endDate: Date | null // null means "ongoing" (e.g., COW_MATURE has no end)
  ageInMonthsAtStart: number
  ageInMonthsAtEnd: number | null
  note?: string // Optional: explain why this period exists
}

type BaseCohortT = {
  id: string

  birthDate: Date
  gender: LivestockGenderT
  tags?: LivestockTagColorT[]
  name?: string
  // species: LivestockSpeciesT

  purpose?: LivestockCohortPurposeT
  eventHistory: {
    lifecycleEventsKnown: LifeCycleEvents
    lifecycleEventsPredicted: LifeCycleEvents
    headcountEvents: HeadcountEvent[]
    weighingEvents: WeighingEvent[]
  }

  growthCurveConfig: GrowthCurveConfig
  seasonalConfig: SeasonalConfig

  projectedGrowthCurveAverage?: GrowthCurvePoint[]
  projectedGrowthCurveAverageLatestWeighingDate?: Date | null
  expectedAgeClassProgression: AgeClassPeriod<CattleAgeClass | SheepAgeClass>[]

  records: {
    cohort: LivestockCohortRecordT
    events: LivestockEventRecordT[]
  }
}

export type CattleCohortT = BaseCohortT & {
  species: 'cattle'
  systemDetails: CattleBreedConfigT
  expectedAgeClassProgression: AgeClassPeriod<CattleAgeClass>[]
}

export type SheepCohortT = BaseCohortT & {
  species: 'sheep'
  systemDetails: SheepBreedConfigT
  expectedAgeClassProgression: AgeClassPeriod<SheepAgeClass>[]
}

export type CohortT = CattleCohortT | SheepCohortT

export type NewCohortT = Omit<CohortT, 'records'> & {
  currentAgeClass: CattleAgeClass | SheepAgeClass
  notes: string
}

export function createCohortFromRecords(options: {
  cohortRecord: LivestockCohortRecordT
  cohortEvents: LivestockEventRecordT[]
  systemDetails: CattleBreedConfigT | SheepBreedConfigT
}): CohortT {
  let lifecycleEventsKnown: LifeCycleEvents = {}
  if (options.cohortRecord.weaned)
    lifecycleEventsKnown.weaned = dayjs(options.cohortRecord.weaned).toDate()
  if (options.cohortRecord.firstJoined)
    lifecycleEventsKnown.firstJoined = dayjs(options.cohortRecord.firstJoined).toDate()
  if (options.cohortRecord.castrated)
    lifecycleEventsKnown.castrated = dayjs(options.cohortRecord.castrated).toDate()

  const sortedEvents = options.cohortEvents.sort((a, b) => {
    if (dayjs(a.eventDate).isBefore(b.eventDate)) return -1
    return 1
  })

  const cohort = {
    ...pick(options.cohortRecord, ['id', 'gender', 'species']),
    name: options.cohortRecord.name ?? undefined,
    tags: options.cohortRecord.tags ?? undefined,
    birthDate: dayjs(options.cohortRecord.birthYearMonth).toDate(),
    purpose: options.cohortRecord.metadata?.purpose ?? undefined,
    eventHistory: {
      lifecycleEventsKnown: lifecycleEventsKnown,
      // placeholder to be computed later
      lifecycleEventsPredicted: {},
      headcountEvents: flat(
        sortedEvents.map((e) => {
          return parseHeadcountEventFromRecord(options.cohortRecord.id, e)
        }),
      ),
      weighingEvents: flat(
        sortedEvents.map((e) => {
          return parseWeightEventFromRecord(options.cohortRecord.id, e)
        }),
      ),
    },

    systemDetails: options.systemDetails,
    growthCurveConfig: getGrowthCurveConfig(options.systemDetails),
    seasonalConfig: getSeasonalConfig(options.systemDetails.state),
    // placeholder to be computed later
    expectedAgeClassProgression: [],

    records: {
      cohort: options.cohortRecord,
      events: options.cohortEvents,
    },
  } as CohortT

  cohort.eventHistory.lifecycleEventsPredicted = projectLifeCycleEventsForCohort(cohort)

  if (cohort.species === 'cattle') {
    cohort.expectedAgeClassProgression = computeExpectedAgeClassProgression<CattleAgeClass>(cohort)
  } else {
    cohort.expectedAgeClassProgression = computeExpectedAgeClassProgression<SheepAgeClass>(cohort)
  }

  const growthCurveEnd = dayjs().add(4, 'years')
  const latestWeighingDate = cohort.eventHistory.weighingEvents.reduce<Date | null>(
    (latest, event) => {
      if (!latest) return event.date
      return dayjs(event.date).isAfter(latest) ? event.date : latest
    },
    null,
  )
  cohort.projectedGrowthCurveAverageLatestWeighingDate = latestWeighingDate
  cohort.projectedGrowthCurveAverage = computeProjectedGrowthCurve(
    cohort,
    growthCurveEnd.toDate(),
    'average',
  )

  return cohort
}

// takes a partial because some things might be absent during cohort creation...
export function computeCohortName(
  cohort: Partial<CohortT>,
  options?: {
    includeBreed?: boolean
  },
) {
  if (cohort.name) return cohort.name

  let out = ''
  if (cohort.birthDate) {
    out = `${dayjs(cohort.birthDate).format("MMM 'YY")}`
  }
  if (cohort.gender)
    out = `${out} ${capitalize(cohort.gender)}${cohort.gender !== 'mixed' ? 's' : ''}`
  if (cohort.tags) out = `${out} ${cohort.tags.join('/')} Tags`
  if (options && options.includeBreed) out = `${out} - ${cohort.systemDetails?.breed}`

  return out.trim()
}

function parseHeadcountEventFromRecord(
  cohortId: string,
  event: LivestockEventRecordT,
): HeadcountEvent[] {
  return event.details
    .filter((d) => d.cohortId === cohortId && d.headcountChange)
    .map((d) => {
      return {
        eventId: event.id,
        date: dayjs(event.eventDate).toDate(),
        headcountChange: d.headcountChange!,
        type: event.type,
      }
    })
}

function parseWeightEventFromRecord(
  cohortId: string,
  event: LivestockEventRecordT,
): WeighingEvent[] {
  return event.details
    .filter((d) => d.cohortId === cohortId)
    .filter((d) => (event.metadata && 'avgWeightKg' in event.metadata) || d.avgWeightKg)
    .map((d) => {
      const weight =
        d.avgWeightKg ??
        (event.metadata && 'avgWeightKg' in event.metadata ? event.metadata.avgWeightKg : undefined)
      return {
        eventId: event.id,
        date: dayjs(event.eventDate).toDate(),
        averageWeight: weight!,
        type: event.type,
      }
    })
}

export function projectLifeCycleEventsForCohort(cohort: CohortT): LifeCycleEvents {
  const birthDate = dayjs(cohort.birthDate)

  let out: LifeCycleEvents = {
    weaned: birthDate.add(cohort.systemDetails.weaningAgeInMonths, 'month').toDate(),
  }

  if (cohort.gender === 'female') {
    out.firstJoined = birthDate.add(cohort.systemDetails.joiningAgeInMonths, 'month').toDate()
  }

  if (cohort.gender === 'male') {
    out.castrated = dayjs(cohort.birthDate).add(6, 'month').toDate()
  }

  return out
}

export function getCohortAgeClassLabel(cohort: CohortT, options?: { date: Date }) {
  const ageClass = computeAgeClass(cohort, options?.date ?? new Date())
  if (cohort.species === 'cattle') {
    return getCattleGenderAgeClassLabel(cohort.gender, ageClass as CattleAgeClass)
  }
  return getSheepGenderAgeClassLabel(cohort.gender, ageClass as SheepAgeClass)
}

export function addNewCohortHeadcountEventFromRecord(
  cohort: CohortT,
  eventRecord: LivestockEventRecordT,
): void {
  const parsedEvents = parseHeadcountEventFromRecord(cohort.id, eventRecord)
  cohort.eventHistory.headcountEvents.push(...parsedEvents)

  cohort.records.events.push(eventRecord)
}

export type ProjectedCohortState = {
  cohort: CohortT
  projectionDate: Date
  projectedState: {
    ageClass: CattleAgeClass | SheepAgeClass
    ageInMonths: number
    headcount: number
    averageLiveweight: number // kg
    // liveweightGainPerDay: number // kg/day
    valuationRatePerKgLwt: number | null // $/kg lwt used for valuation; null if no market data for this date
    estimatedValue: number | null
    estimatedValuePerHead: number | null
  }
}

export function computeCohortStateAtDate(options: {
  cohort: CohortT
  date: Date
  marketCondition?: MarketCondition
}): ProjectedCohortState {
  const projectedHeadcount = computeHeadcountAtDate(options.cohort, options.date)
  const projectedAgeClass = computeAgeClass(options.cohort, options.date)
  const projectedAvgLiveweight = projectCohortWeight(
    options.cohort,
    options.date,
    options.marketCondition,
  )
  const valuationRates = getRatesForDate(options.date)?.[options.cohort.species] ?? null
  const valuationRatePerKgLwt = valuationRates
    ? ((valuationRates as Record<string, number>)[projectedAgeClass] ?? null)
    : null

  return {
    cohort: options.cohort,
    projectionDate: options.date,
    projectedState: {
      ageClass: projectedAgeClass,
      ageInMonths: computeAgeInMonths(options.cohort, options.date),
      headcount: projectedHeadcount,
      averageLiveweight: projectedAvgLiveweight,
      // liveweightGainPerDay: number // kg/day
      valuationRatePerKgLwt,
      estimatedValue:
        valuationRates === null
          ? null
          : computeValue({
              valuationRates,
              ageClass: projectedAgeClass,
              averageLiveweight: projectedAvgLiveweight,
              headcount: projectedHeadcount,
            }),
      estimatedValuePerHead:
        valuationRates === null
          ? null
          : computeValuePerHead({
              valuationRates,
              ageClass: projectedAgeClass,
              averageLiveweight: projectedAvgLiveweight,
            }),
    },
  }
}

export function projectCohortWeight(
  cohort: CohortT,
  dateUntil: Date,
  marketCondition?: MarketCondition,
): number {
  const growthCurve = computeProjectedGrowthCurve(cohort, dateUntil, marketCondition)
  return growthCurve.at(growthCurve.length - 1)?.weight ?? 0
}

function computeValue(options: {
  valuationRates: Record<string, number>
  ageClass: CattleAgeClass | SheepAgeClass
  averageLiveweight: number
  headcount: number
}) {
  const pricePerKg = options.valuationRates[options.ageClass] ?? 0
  return options.headcount * options.averageLiveweight * pricePerKg
}

function computeValuePerHead(options: {
  valuationRates: Record<string, number>
  ageClass: CattleAgeClass | SheepAgeClass
  averageLiveweight: number
}) {
  const pricePerKg = options.valuationRates[options.ageClass] ?? 0
  return options.averageLiveweight * pricePerKg
}

function computeAgeInMonths(cohort: CohortT, atDate: Date): number {
  return Math.abs(dayjs(atDate).diff(dayjs(cohort.birthDate), 'month'))
}

function computeHeadcountAtDate(cohort: CohortT, atDate: Date): number {
  const sortedHeadcountEvents = [...cohort.eventHistory.headcountEvents].sort(
    (a, b) => a.date.getTime() - b.date.getTime(),
  )

  let cumulativeHeadcount = 0
  for (const event of sortedHeadcountEvents) {
    if (event.date <= atDate) {
      cumulativeHeadcount += event.headcountChange
    } else {
      break
    }
  }

  return Math.max(0, cumulativeHeadcount)
}

export function computeExpectedAgeClassProgression<
  SpeciesAgeClass extends CattleAgeClass | SheepAgeClass,
>(cohort: CohortT): AgeClassPeriod<SpeciesAgeClass>[] {
  if (cohort.species === 'cattle') {
    return computeCattleAgeClassProgression(cohort) as AgeClassPeriod<SpeciesAgeClass>[]
  }
  return computeSheepAgeClassProgression(cohort) as AgeClassPeriod<SpeciesAgeClass>[]
}

function computeCattleAgeClassProgression(cohort: CattleCohortT): AgeClassPeriod<CattleAgeClass>[] {
  const progression: AgeClassPeriod<CattleAgeClass>[] = []

  // Merge known and predicted lifecycle events
  const lifecycleEvents = mergeDeep(
    clone(cohort.eventHistory.lifecycleEventsKnown),
    cohort.eventHistory.lifecycleEventsPredicted,
  )

  const birthDate = dayjs(cohort.birthDate)

  // Calculate key lifecycle dates
  const weaningDate = lifecycleEvents.weaned
    ? dayjs(lifecycleEvents.weaned)
    : birthDate.add(cohort.systemDetails.weaningAgeInMonths, 'months')

  // CALF_UNWEANED: Birth to weaning
  progression.push({
    ageClass: CattleAgeClass.CALF_UNWEANED,
    startDate: birthDate.toDate(),
    endDate: weaningDate.toDate(),
    ageInMonthsAtStart: 0,
    ageInMonthsAtEnd: cohort.systemDetails.weaningAgeInMonths,
    note: 'On mother, rapid growth from milk + pasture',
  })

  // Different progression for males vs females
  if (cohort.gender === 'male') {
    return computeCattleMaleProgression(cohort, progression, weaningDate, lifecycleEvents)
  } else {
    return computeCattleFemaleProgression(cohort, progression, weaningDate, lifecycleEvents)
  }
}

function computeCattleMaleProgression(
  cohort: CohortT,
  progression: AgeClassPeriod<CattleAgeClass>[],
  weaningDate: dayjs.Dayjs,
  lifecycleEvents: LifeCycleEvents,
): AgeClassPeriod<CattleAgeClass>[] {
  const birthDate = dayjs(cohort.birthDate)

  // const _castrationDate = lifecycleEvents.castrated
  //   ? dayjs(lifecycleEvents.castrated)
  //   : weaningDate.add(1, 'month') // Typically castrate soon after weaning

  const isCastrated = !!lifecycleEvents.castrated

  if (isCastrated) {
    // WEANERS: Weaning to 12 months
    const weanerEnd = birthDate.add(12, 'months')
    progression.push({
      ageClass: CattleAgeClass.WEANERS,
      startDate: weaningDate.toDate(),
      endDate: weanerEnd.toDate(),
      ageInMonthsAtStart: cohort.systemDetails.weaningAgeInMonths,
      ageInMonthsAtEnd: 12,
      note: 'Post-weaning growth phase, castrated',
    })

    // YEARLINGS: 12-18 months
    const yearlingEnd = birthDate.add(18, 'months')
    progression.push({
      ageClass: CattleAgeClass.YEARLINGS,
      startDate: weanerEnd.toDate(),
      endDate: yearlingEnd.toDate(),
      ageInMonthsAtStart: 12,
      ageInMonthsAtEnd: 18,
      note: 'Backgrounding phase',
    })

    // STEER_18_24M: 18-24 months
    const steer18_24End = birthDate.add(24, 'months')
    progression.push({
      ageClass: CattleAgeClass.STEER_18_24M,
      startDate: yearlingEnd.toDate(),
      endDate: steer18_24End.toDate(),
      ageInMonthsAtStart: 18,
      ageInMonthsAtEnd: 24,
      note: 'Prime finishing age, often sold',
    })

    // STEER_24_36M: 24-36+ months
    progression.push({
      ageClass: CattleAgeClass.STEER_24_36M,
      startDate: steer18_24End.toDate(),
      endDate: null, // Ongoing
      ageInMonthsAtStart: 24,
      ageInMonthsAtEnd: null,
      note: 'Heavy steer, premium grassfed market (if retained)',
    })
  } else {
    // BULL progression (not castrated)
    const weanerEnd = birthDate.add(12, 'months')
    progression.push({
      ageClass: CattleAgeClass.WEANERS,
      startDate: weaningDate.toDate(),
      endDate: weanerEnd.toDate(),
      ageInMonthsAtStart: cohort.systemDetails.weaningAgeInMonths,
      ageInMonthsAtEnd: 12,
      note: 'Post-weaning, entire male (not castrated)',
    })

    // BULL_BREEDING: 12+ months (or whenever they enter breeding herd)
    progression.push({
      ageClass: CattleAgeClass.BULL_BREEDING,
      startDate: weanerEnd.toDate(),
      endDate: null,
      ageInMonthsAtStart: 12,
      ageInMonthsAtEnd: null,
      note: 'Breeding bull or growing entire male',
    })
  }

  return progression
}

function computeCattleFemaleProgression(
  cohort: CohortT,
  progression: AgeClassPeriod<CattleAgeClass>[],
  weaningDate: dayjs.Dayjs,
  lifecycleEvents: LifeCycleEvents,
): AgeClassPeriod<CattleAgeClass>[] {
  const birthDate = dayjs(cohort.birthDate)

  // WEANERS: Weaning to 12 months
  const weanerEnd = birthDate.add(12, 'months')
  progression.push({
    ageClass: CattleAgeClass.WEANERS,
    startDate: weaningDate.toDate(),
    endDate: weanerEnd.toDate(),
    ageInMonthsAtStart: cohort.systemDetails.weaningAgeInMonths,
    ageInMonthsAtEnd: 12,
    note:
      cohort.purpose === 'breeding'
        ? 'Post-weaning growth, replacement heifer potential'
        : 'Post-weaning growth, production animal',
  })

  // If production purpose, stop at yearlings (won't be joined)
  if (cohort.purpose === 'selling') {
    progression.push({
      ageClass: CattleAgeClass.YEARLINGS,
      startDate: weanerEnd.toDate(),
      endDate: null, // Continues until sale
      ageInMonthsAtStart: 12,
      ageInMonthsAtEnd: null,
      note: 'Growing for sale, not for breeding',
    })
    return progression
  }

  // Determine joining date
  const joiningAge = cohort.systemDetails.joiningAgeInMonths
  const joiningDate = lifecycleEvents.firstJoined
    ? dayjs(lifecycleEvents.firstJoined)
    : birthDate.add(joiningAge, 'months')

  // YEARLINGS: 12 months to joining (or 24 months if not joined)
  const yearlingEnd = joiningDate.isBefore(birthDate.add(24, 'months'))
    ? joiningDate
    : birthDate.add(24, 'months')

  progression.push({
    ageClass: CattleAgeClass.YEARLINGS,
    startDate: weanerEnd.toDate(),
    endDate: yearlingEnd.toDate(),
    ageInMonthsAtStart: 12,
    ageInMonthsAtEnd: yearlingEnd.diff(birthDate, 'months'),
    note:
      joiningAge < 24
        ? `Growing to joining age (${joiningAge} months)`
        : 'Growing phase, not yet joined',
  })

  // If joining age is 24+, add HEIFER classes
  if (joiningAge >= 24) {
    // HEIFER_24_30M
    if (joiningAge < 30) {
      const heifer24_30End = joiningDate
      progression.push({
        ageClass: CattleAgeClass.HEIFER_24_30M,
        startDate: birthDate.add(24, 'months').toDate(),
        endDate: heifer24_30End.toDate(),
        ageInMonthsAtStart: 24,
        ageInMonthsAtEnd: heifer24_30End.diff(birthDate, 'months'),
        note: `Unmated heifer, approaching joining age (${joiningAge}mo)`,
      })
    } else {
      // Full 24-30 month period
      const heifer24_30End = birthDate.add(30, 'months')
      progression.push({
        ageClass: CattleAgeClass.HEIFER_24_30M,
        startDate: birthDate.add(24, 'months').toDate(),
        endDate: heifer24_30End.toDate(),
        ageInMonthsAtStart: 24,
        ageInMonthsAtEnd: 30,
        note: 'Unmated heifer, common in tropical systems',
      })

      // HEIFER_30_36M
      if (joiningAge >= 30) {
        const heifer30_36End = joiningDate
        progression.push({
          ageClass: CattleAgeClass.HEIFER_30_36M,
          startDate: heifer24_30End.toDate(),
          endDate: heifer30_36End.toDate(),
          ageInMonthsAtStart: 30,
          ageInMonthsAtEnd: heifer30_36End.diff(birthDate, 'months'),
          note: `Late-joining heifer system (${joiningAge}mo joining)`,
        })
      }
    }
  }

  // Calculate calving dates
  const gestationMonths = 9
  const weaningMonths = 6
  const rebreedingMonths = 3 // Time to reconceive after weaning
  const calvingInterval = weaningMonths + rebreedingMonths + gestationMonths // = 18 months

  const firstCalvingDate = joiningDate.add(gestationMonths, 'months')
  const secondCalvingDate = firstCalvingDate.add(calvingInterval, 'months')

  // If joined before first calving - might be pregnant heifer
  if (joiningDate.isBefore(firstCalvingDate)) {
    const pregnantHeiferStart = joiningDate
    const pregnantHeiferEnd = firstCalvingDate

    // Determine which heifer class based on age at joining
    const ageAtJoining = joiningDate.diff(birthDate, 'months')
    let pregnantClass: CattleAgeClass

    if (ageAtJoining < 24) {
      pregnantClass = CattleAgeClass.YEARLINGS
    } else if (ageAtJoining < 30) {
      pregnantClass = CattleAgeClass.HEIFER_24_30M
    } else {
      pregnantClass = CattleAgeClass.HEIFER_30_36M
    }

    // Only add if we haven't already covered this period
    const lastPeriod = progression[progression.length - 1]
    if (dayjs(lastPeriod?.endDate).isBefore(pregnantHeiferEnd)) {
      progression.push({
        ageClass: pregnantClass,
        startDate: pregnantHeiferStart.toDate(),
        endDate: pregnantHeiferEnd.toDate(),
        ageInMonthsAtStart: ageAtJoining,
        ageInMonthsAtEnd: firstCalvingDate.diff(birthDate, 'months'),
        note: 'Pregnant with first calf',
      })
    }
  }

  // COW_FIRST_CALF: First calving to second calving
  progression.push({
    ageClass: CattleAgeClass.COW_FIRST_CALF,
    startDate: firstCalvingDate.toDate(),
    endDate: secondCalvingDate.toDate(),
    ageInMonthsAtStart: firstCalvingDate.diff(birthDate, 'months'),
    ageInMonthsAtEnd: secondCalvingDate.diff(birthDate, 'months'),
    note: 'First-calf heifer, lactating and growing, needs to reconceive',
  })

  // COW_MATURE: After second calving
  progression.push({
    ageClass: CattleAgeClass.COW_MATURE,
    startDate: secondCalvingDate.toDate(),
    endDate: null, // Ongoing
    ageInMonthsAtStart: secondCalvingDate.diff(birthDate, 'months'),
    ageInMonthsAtEnd: null,
    note: 'Mature breeding cow, proven breeder',
  })

  return progression
}

export function computeSheepAgeClassProgression(
  sheep: SheepCohortT,
): AgeClassPeriod<SheepAgeClass>[] {
  const progression: AgeClassPeriod<SheepAgeClass>[] = []
  const birthDate = dayjs(sheep.birthDate)

  // Merge lifecycle events
  const events = {
    ...sheep.eventHistory.lifecycleEventsKnown,
    ...sheep.eventHistory.lifecycleEventsPredicted,
  }

  const weaningDate = events.weaned
    ? dayjs(events.weaned)
    : birthDate.add(sheep.systemDetails.weaningAgeInMonths, 'months')

  // LAMB_UNWEANED: Birth to weaning
  progression.push({
    ageClass: SheepAgeClass.LAMB_UNWEANED,
    startDate: birthDate.toDate(),
    endDate: weaningDate.toDate(),
    ageInMonthsAtStart: 0,
    ageInMonthsAtEnd: sheep.systemDetails.weaningAgeInMonths,
    note: 'On mother, high growth rate',
  })

  if (sheep.gender === 'male') {
    return computeSheepMaleProgression(sheep, progression, weaningDate, events)
  } else {
    return computeSheepFemaleProgression(sheep, progression, weaningDate, events)
  }
}

function computeSheepMaleProgression(
  sheep: SheepCohortT,
  progression: AgeClassPeriod<SheepAgeClass>[],
  weaningDate: dayjs.Dayjs,
  events: LifeCycleEvents,
): AgeClassPeriod<SheepAgeClass>[] {
  const birthDate = dayjs(sheep.birthDate)
  const isCastrated = !!events.castrated

  if (isCastrated) {
    // Wethers
    const hoggetStart = birthDate.add(12, 'months')

    progression.push({
      ageClass: SheepAgeClass.LAMB_WEANER,
      startDate: weaningDate.toDate(),
      endDate: hoggetStart.toDate(),
      ageInMonthsAtStart: sheep.systemDetails.weaningAgeInMonths,
      ageInMonthsAtEnd: 12,
      note: 'Weaner wether, typically sold by 12-18 months',
    })

    const wether24M = birthDate.add(24, 'months')

    progression.push({
      ageClass: SheepAgeClass.WETHER_12_24M,
      startDate: hoggetStart.toDate(),
      endDate: wether24M.toDate(),
      ageInMonthsAtStart: 12,
      ageInMonthsAtEnd: 24,
      note: 'Hogget wether, prime finishing age',
    })

    progression.push({
      ageClass: SheepAgeClass.WETHER_24M_PLUS,
      startDate: wether24M.toDate(),
      endDate: null,
      ageInMonthsAtStart: 24,
      ageInMonthsAtEnd: null,
      note: 'Older wether (uncommon, usually sold earlier)',
    })
  } else {
    // Rams
    const hoggetStart = birthDate.add(12, 'months')

    progression.push({
      ageClass: SheepAgeClass.LAMB_WEANER,
      startDate: weaningDate.toDate(),
      endDate: hoggetStart.toDate(),
      ageInMonthsAtStart: sheep.systemDetails.weaningAgeInMonths,
      ageInMonthsAtEnd: 12,
      note: 'Ram lamb, potential breeding stock',
    })

    progression.push({
      ageClass: SheepAgeClass.RAM_BREEDING,
      startDate: hoggetStart.toDate(),
      endDate: null,
      ageInMonthsAtStart: 12,
      ageInMonthsAtEnd: null,
      note: 'Breeding ram',
    })
  }

  return progression
}

function computeSheepFemaleProgression(
  sheep: SheepCohortT,
  progression: AgeClassPeriod<SheepAgeClass>[],
  weaningDate: dayjs.Dayjs,
  events: LifeCycleEvents,
): AgeClassPeriod<SheepAgeClass>[] {
  const birthDate = dayjs(sheep.birthDate)
  const joiningAge = sheep.systemDetails.joiningAgeInMonths
  const gestationMonths = 5 // Sheep gestation

  const joiningDate = events.firstJoined
    ? dayjs(events.firstJoined)
    : birthDate.add(joiningAge, 'months')

  // LAMB_WEANER to joining or 12 months
  const weanerEnd = joiningDate.isBefore(birthDate.add(12, 'months'))
    ? joiningDate
    : birthDate.add(12, 'months')

  progression.push({
    ageClass: SheepAgeClass.LAMB_WEANER,
    startDate: weaningDate.toDate(),
    endDate: weanerEnd.toDate(),
    ageInMonthsAtStart: sheep.systemDetails.weaningAgeInMonths,
    ageInMonthsAtEnd: weanerEnd.diff(birthDate, 'months'),
    note: joiningAge < 12 ? 'Growing to early joining' : 'Weaner ewe lamb',
  })

  // If production purpose, stop here or at hogget
  if (sheep.purpose === 'selling') {
    progression.push({
      ageClass: SheepAgeClass.HOGGET,
      startDate: weanerEnd.toDate(),
      endDate: null, // Continues until sale
      ageInMonthsAtStart: 12,
      ageInMonthsAtEnd: null,
      note: 'Growing for sale, not for breeding',
    })
    return progression
  }

  const firstLambingDate = joiningDate.add(gestationMonths, 'months')

  // If joining after 12 months, add HOGGET or MAIDEN_EWE
  if (joiningAge >= 12) {
    const maidenEnd = joiningDate

    progression.push({
      ageClass: joiningAge >= 18 ? SheepAgeClass.MAIDEN_EWE : SheepAgeClass.HOGGET,
      startDate: birthDate.add(12, 'months').toDate(),
      endDate: maidenEnd.toDate(),
      ageInMonthsAtStart: 12,
      ageInMonthsAtEnd: maidenEnd.diff(birthDate, 'months'),
      note: `Growing to joining age (${joiningAge} months)`,
    })

    // ADD THIS: Pregnant hogget/maiden period from joining to first lambing
    progression.push({
      ageClass: joiningAge >= 18 ? SheepAgeClass.MAIDEN_EWE : SheepAgeClass.HOGGET,
      startDate: joiningDate.toDate(),
      endDate: firstLambingDate.toDate(),
      ageInMonthsAtStart: joiningDate.diff(birthDate, 'months'),
      ageInMonthsAtEnd: firstLambingDate.diff(birthDate, 'months'),
      note: 'Pregnant with first lamb',
    })
  }

  const secondLambingDate = firstLambingDate.add(12, 'months') // Annual lambing

  // EWE_FIRST_LAMB: First lambing to second lambing
  progression.push({
    ageClass: SheepAgeClass.EWE_FIRST_LAMB,
    startDate: firstLambingDate.toDate(),
    endDate: secondLambingDate.toDate(),
    ageInMonthsAtStart: firstLambingDate.diff(birthDate, 'months'),
    ageInMonthsAtEnd: secondLambingDate.diff(birthDate, 'months'),
    note: 'First-time mother, still growing',
  })

  // EWE_MATURE: After second lambing
  progression.push({
    ageClass: SheepAgeClass.EWE_MATURE,
    startDate: secondLambingDate.toDate(),
    endDate: null,
    ageInMonthsAtStart: secondLambingDate.diff(birthDate, 'months'),
    ageInMonthsAtEnd: null,
    note: 'Mature breeding ewe',
  })

  return progression
}

// Helper function to get age class at specific date (uses the progression)
export function computeAgeClass(cohort: CohortT, atDate: Date): CattleAgeClass | SheepAgeClass {
  const targetDate = dayjs(atDate)

  for (const period of cohort.expectedAgeClassProgression) {
    const start = dayjs(period.startDate)
    const end = period.endDate ? dayjs(period.endDate) : null

    if (
      (targetDate.isSame(start, 'week') || targetDate.isAfter(start)) &&
      targetDate.isBefore(end)
    ) {
      return period.ageClass
    }
  }

  // Fallback (shouldn't reach here)
  return cohort.expectedAgeClassProgression.at(cohort.expectedAgeClassProgression.length - 1)!
    .ageClass
}

export function isInFinalAgeClass(cohort: CohortT, date: Date): boolean {
  const targetDate = dayjs(date)

  for (const period of cohort.expectedAgeClassProgression) {
    const start = dayjs(period.startDate)
    const end = period.endDate ? dayjs(period.endDate) : null

    if (
      (targetDate.isSame(start, 'week') || targetDate.isAfter(start)) &&
      (end === null || targetDate.isBefore(end))
    ) {
      return period.endDate === null
    }
  }

  return false
}

export type CohortGroupingKey = {
  ageClass: CattleAgeClass | SheepAgeClass
  purpose: LivestockCohortPurposeT | undefined
  breed: string
}

export function getCohortGroupingKey(cohort: CohortT, date: Date): CohortGroupingKey | null {
  if (!isInFinalAgeClass(cohort, date)) return null

  return {
    ageClass: computeAgeClass(cohort, date),
    purpose: cohort.purpose,
    breed: cohort.systemDetails.breed,
  }
}

export function serializeCohortGroupingKey(key: CohortGroupingKey): string {
  return `${key.ageClass}:${key.purpose ?? 'none'}:${key.breed}`
}

export type GrowthCurvePoint = {
  weight: number
  date: Date
  isActual?: boolean
  source?: 'birth' | 'weighing' | 'projection'
  variance?: number // How far off was our projection from actual weighing
}

export function computeProjectedGrowthCurve(
  cohort: CohortT,
  targetDate: Date,
  marketCondition?: MarketCondition,
): GrowthCurvePoint[] {
  if (!marketCondition || marketCondition === 'average') {
    const cached = getCachedAverageGrowthCurve(cohort, targetDate)
    if (cached) return cached
  }
  const weighingEvents = cohort.eventHistory.weighingEvents || []
  const sortedWeighings = [...weighingEvents]
    .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
    .filter((w) => new Date(w.date) <= targetDate)

  // PASS 1: Pure projection from birth
  const pureCurve = projectPureGrowth(cohort, targetDate, marketCondition)

  // PASS 2: Blend in actual weighings
  const blendedCurve = blendWithActualWeighings(pureCurve, sortedWeighings)

  return blendedCurve
}

function getCachedAverageGrowthCurve(cohort: CohortT, targetDate: Date): GrowthCurvePoint[] | null {
  const cached = cohort.projectedGrowthCurveAverage
  if (!cached || cached.length === 0) return null

  const latestWeighing = cohort.eventHistory.weighingEvents.reduce<Date | null>((latest, event) => {
    if (!latest) return event.date
    return dayjs(event.date).isAfter(latest) ? event.date : latest
  }, null)
  const cachedWeighing = cohort.projectedGrowthCurveAverageLatestWeighingDate
  if (cachedWeighing && latestWeighing && !dayjs(cachedWeighing).isSame(latestWeighing, 'day'))
    return null
  if (!cachedWeighing && latestWeighing) return null

  const lastPointDate = cached.at(cached.length - 1)?.date
  if (!lastPointDate || dayjs(lastPointDate).isBefore(targetDate, 'day')) return null

  return cached.filter((point) => point.date <= targetDate)
}

export type CohortLineItemMatchEvaluation = {
  cohortId: string
  score: number // 0-1 composite score
  eligible: boolean
  reasons: string[]
  components: {
    classFit: number
    headcountFit: number
    weightFit?: number
    textFit?: number
  }
  projected: {
    headcount: number
    averageLiveweight: number
    ageClass: CattleAgeClass | SheepAgeClass
    weightSource: 'projection' | 'weighing'
    weightSampleAgeDays?: number
  }
}

export function evaluateCohortMatchForParsedLine(options: {
  cohort: CohortT
  lineItem: ParsedSalePurchaseLineItemT
  saleDate: Date
}): CohortLineItemMatchEvaluation {
  const saleDate = dayjs(options.saleDate).toDate()
  const state = computeCohortStateAtDate({ cohort: options.cohort, date: saleDate })
  const projectedHeadcount = state.projectedState.headcount
  const projectedAgeClass = state.projectedState.ageClass
  const reasons: string[] = []

  if (projectedHeadcount < options.lineItem.headcount) {
    reasons.push(
      `Insufficient headcount: ${projectedHeadcount} available, ${options.lineItem.headcount} required`,
    )
    return {
      cohortId: options.cohort.id,
      score: 0,
      eligible: false,
      reasons,
      components: {
        classFit: 0,
        headcountFit: 0,
      },
      projected: {
        headcount: projectedHeadcount,
        averageLiveweight: state.projectedState.averageLiveweight,
        ageClass: projectedAgeClass,
        weightSource: 'projection',
      },
    }
  }

  const hints = inferLineHints(options.lineItem)
  const classFitRaw = computeAgeClassFitScore(projectedAgeClass, options.cohort.gender, hints)
  const classFit = (classFitRaw + 1) / 2 // normalise to 0-1

  const headcountFit = computeHeadcountFitScore(projectedHeadcount, options.lineItem.headcount)

  const weightBasis = pickWeightBasis(
    options.cohort,
    saleDate,
    state.projectedState.averageLiveweight,
  )
  let weightFit: number | undefined
  if (typeof options.lineItem.avgWeight === 'number') {
    const weightDelta = Math.abs(weightBasis.weight - options.lineItem.avgWeight)
    const heavyClasses = new Set<CattleAgeClass | SheepAgeClass>([
      CattleAgeClass.COW_MATURE,
      CattleAgeClass.STEER_24_36M,
      CattleAgeClass.BULL_BREEDING,
    ])
    const sigma = heavyClasses.has(projectedAgeClass) ? 120 : 80
    weightFit = Math.max(0, Math.exp(-weightDelta / sigma))
    reasons.push(
      `${weightBasis.source === 'weighing' ? 'Recent weighing' : 'Projected weight'} ${weightBasis.weight.toFixed(0)}kg vs line ${options.lineItem.avgWeight.toFixed(0)}kg`,
    )
  }

  const textFit = computeTextFitScore(hints.tokens, options.cohort)

  if (classFitRaw > 0.3) {
    reasons.push('Class/gender hint matches parsed line')
  } else if (classFitRaw < -0.3) {
    reasons.push('Class/gender hint conflicts with parsed line')
  }

  reasons.push(
    `Headcount fits: line ${options.lineItem.headcount} vs projected ${projectedHeadcount}`,
  )

  if (textFit > 0) {
    reasons.push('Name/id hints overlap with cohort name or tags')
  }

  const weightContribution =
    typeof options.lineItem.avgWeight === 'number' ? 0.3 * (weightFit ?? 0) : 0.15 // keep some weight for absence of data

  const score = clamp01(0.4 * classFit + 0.2 * headcountFit + weightContribution + 0.1 * textFit)

  return {
    cohortId: options.cohort.id,
    score,
    eligible: true,
    reasons,
    components: {
      classFit,
      headcountFit,
      weightFit,
      textFit,
    },
    projected: {
      headcount: projectedHeadcount,
      averageLiveweight: weightBasis.weight,
      ageClass: projectedAgeClass,
      weightSource: weightBasis.source,
      weightSampleAgeDays: weightBasis.daysFromSale,
    },
  }
}

type LineHints = {
  genderHint?: LivestockGenderT | 'mixed'
  ageHint?: 'heifer' | 'cow' | 'steer' | 'bull'
  tokens: string[]
}

function inferLineHints(lineItem: ParsedSalePurchaseLineItemT): LineHints {
  const name = `${lineItem.name ?? ''} ${lineItem.description ?? ''}`.toLowerCase()
  const idPart = lineItem.id?.toLowerCase() ?? ''
  const tokens: string[] = `${name} ${idPart}`.match(/[a-z0-9]+/g) ?? []

  const hints: LineHints = { tokens }

  if (name.includes('heifer')) {
    hints.genderHint = 'female'
    hints.ageHint = 'heifer'
  }
  if (name.includes('cow')) {
    hints.genderHint = 'female'
    hints.ageHint = 'cow'
  }
  if (name.includes('steer')) {
    hints.genderHint = 'male'
    hints.ageHint = 'steer'
  }
  if (name.includes('bull')) {
    hints.genderHint = 'male'
    hints.ageHint = 'bull'
  }

  // Handle simple abbreviations often used in saleyard slips
  if (!hints.ageHint && tokens.includes('heif')) hints.ageHint = 'heifer'
  if (!hints.ageHint && tokens.includes('c')) hints.ageHint = 'cow'
  if (!hints.ageHint && tokens.includes('st')) hints.ageHint = 'steer'

  return hints
}

function computeAgeClassFitScore(
  ageClass: CattleAgeClass | SheepAgeClass,
  gender: LivestockGenderT,
  hints: LineHints,
): number {
  let score = 0

  if (hints.genderHint) {
    if (gender === hints.genderHint || (gender === 'mixed' && hints.genderHint)) {
      score += 0.6
    } else {
      score -= 0.8
    }
  }

  if (hints.ageHint) {
    const heiferClasses = new Set<CattleAgeClass | SheepAgeClass>([
      CattleAgeClass.WEANERS,
      CattleAgeClass.YEARLINGS,
      CattleAgeClass.HEIFER_24_30M,
      CattleAgeClass.HEIFER_30_36M,
      CattleAgeClass.COW_FIRST_CALF,
    ])
    const cowClasses = new Set<CattleAgeClass | SheepAgeClass>([
      CattleAgeClass.COW_FIRST_CALF,
      CattleAgeClass.COW_MATURE,
    ])
    const steerClasses = new Set<CattleAgeClass | SheepAgeClass>([
      CattleAgeClass.WEANERS,
      CattleAgeClass.YEARLINGS,
      CattleAgeClass.STEER_18_24M,
      CattleAgeClass.STEER_24_36M,
    ])

    const bullClasses = new Set<CattleAgeClass | SheepAgeClass>([
      CattleAgeClass.BULL_BREEDING,
      CattleAgeClass.YEARLINGS,
    ])

    const classSets: Record<string, Set<CattleAgeClass | SheepAgeClass>> = {
      heifer: heiferClasses,
      cow: cowClasses,
      steer: steerClasses,
      bull: bullClasses,
    }

    const matchSet = classSets[hints.ageHint]
    if (matchSet?.has(ageClass)) {
      score += 0.6
    } else {
      score -= 0.4
    }
  }

  return Math.max(-1, Math.min(1, score))
}

function computeHeadcountFitScore(projected: number, lineHeadcount: number): number {
  if (projected <= 0) return 0
  const diffRatio = Math.abs(projected - lineHeadcount) / Math.max(projected, lineHeadcount)
  return Math.max(0, 1 - diffRatio)
}

type WeightBasis = {
  weight: number
  source: 'projection' | 'weighing'
  daysFromSale?: number
}

function pickWeightBasis(cohort: CohortT, saleDate: Date, projectedWeight: number): WeightBasis {
  const sale = dayjs(saleDate)
  const nearestWeighing = [...(cohort.eventHistory.weighingEvents || [])]
    .map((w) => ({
      ...w,
      daysFromSale: Math.abs(dayjs(w.date).diff(sale, 'day')),
    }))
    .sort((a, b) => a.daysFromSale - b.daysFromSale)[0]

  if (nearestWeighing && nearestWeighing.daysFromSale <= 60) {
    return {
      weight: nearestWeighing.averageWeight,
      source: 'weighing',
      daysFromSale: nearestWeighing.daysFromSale,
    }
  }

  return {
    weight: projectedWeight,
    source: 'projection',
  }
}

function computeTextFitScore(tokens: string[], cohort: CohortT): number {
  if (tokens.length === 0) return 0
  const cohortName = (cohort.name ?? '').toLowerCase()
  const cohortTags = (cohort.tags ?? []).map((tag) => tag.toLowerCase())

  let matches = 0
  for (const token of tokens) {
    if (token.length < 2) continue
    if (cohortName && cohortName.includes(token)) {
      matches += 1
      continue
    }
    if (cohortTags.some((tag) => tag.includes(token))) {
      matches += 0.5
    }
  }

  return Math.min(1, matches / 3)
}

function clamp01(value: number): number {
  return Math.max(0, Math.min(1, value))
}

function projectPureGrowth(
  cohort: CohortT,
  targetDate: Date,
  marketCondition?: MarketCondition,
): GrowthCurvePoint[] {
  const curve: GrowthCurvePoint[] = []
  let currentWeight = cohort.species === 'cattle' ? 40 : 5
  let currentDate = cohort.birthDate

  curve.push({
    weight: currentWeight,
    date: new Date(currentDate),
    isActual: true,
    source: 'birth',
  })

  currentDate = dayjs(currentDate).add(1, 'day').toDate()

  while (currentDate <= targetDate) {
    const ageClass = computeAgeClass(cohort, currentDate)
    const ageInMonths = dayjs(currentDate).diff(cohort.birthDate, 'months')

    const dailyGrowth = calculateDailyGrowth(
      ageClass,
      currentWeight,
      currentDate,
      ageInMonths,
      cohort.growthCurveConfig,
      cohort.seasonalConfig,
      marketCondition,
    )

    // Debug every 3 months
    // if (curve.length % 90 === 0) {
    //   const percentMature = (currentWeight / cohort.growthCurveConfig.matureWeight * 100).toFixed(1)
    //   console.log(
    //     `${dayjs(currentDate).format('MMM YYYY')} | ` +
    //     `Age: ${ageInMonths}mo | ` +
    //     `Class: ${ageClass.padEnd(20)} | ` +
    //     `Weight: ${currentWeight.toFixed(1)}kg (${percentMature}% mature) | ` +
    //     `Daily gain: ${dailyGrowth.toFixed(3)}kg`
    //   )
    // }

    currentWeight += dailyGrowth

    curve.push({
      weight: currentWeight,
      date: new Date(currentDate),
      isActual: false,
      source: 'projection',
    })

    currentDate = dayjs(currentDate).add(1, 'day').toDate()
  }

  return curve
}

function blendWithActualWeighings(
  curve: GrowthCurvePoint[],
  weighings: WeighingEvent[],
): GrowthCurvePoint[] {
  if (weighings.length === 0) return curve

  const blendedCurve = [...curve]
  const blendWindow = 180 // Days before/after weighing to blend

  for (const weighing of weighings) {
    // Find the index of this weighing date in curve
    const weighingIndex = curve.findIndex((p) => {
      const pDate = dayjs(p.date)
      const wDate = dayjs(weighing.date)
      return pDate.isSame(wDate, 'day')
    })

    if (weighingIndex === -1) continue

    // Replace with actual weighing
    const projectedWeight = curve[weighingIndex]!.weight
    const variance = weighing.averageWeight - projectedWeight

    blendedCurve[weighingIndex] = {
      weight: weighing.averageWeight,
      date: new Date(weighing.date),
      isActual: true,
      source: 'weighing',
      variance: variance,
    }

    // Blend surrounding points
    for (
      let i = Math.max(0, weighingIndex - blendWindow);
      i < Math.min(curve.length, weighingIndex + blendWindow);
      i++
    ) {
      if (i === weighingIndex) continue

      // Calculate blend factor (1.0 at weighing, 0.0 at edge of window)
      const distance = Math.abs(i - weighingIndex)
      const blendFactor = 1 - distance / blendWindow

      const point = curve[i]!
      const projected = point.weight
      const adjusted = projected + variance * blendFactor

      blendedCurve[i] = {
        ...point,
        weight: adjusted,
      }
    }
  }

  return blendedCurve
}
